import { Button, Flex, FormControl, FormErrorMessage } from "@chakra-ui/react";
import { FormProvider, useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { AddIcon } from "@chakra-ui/icons";
import { User } from "../../types/user.types";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useImperativeHandle } from "react";
import Select from "../inputs/Select";
import _ from "lodash";

type InviteUserFormProps = {
	onSubmit: (user: Partial<User>) => void;
	defaultValue?: Partial<User>;
};

export type InviteUserFormHandle = {
	values: () => Partial<User>;
	isValid: () => Promise<boolean>;
	isDirty: () => boolean;
};

const userSchema = z.object({
	firstName: z.string().min(1, { message: "Ce champ est requis." }),
	lastName: z.string().min(1, { message: "Ce champ est requis." }),
	email: z
		.string()
		.min(1, { message: "Ce champ est requis." })
		.email({ message: "Ce n'est pas un email valide." }),
	role: z.string().min(1, { message: "Ce champ est requis." }),
	access: z.string().min(1, { message: "Ce champ est requis." }),
});

const InviteUserForm = forwardRef<InviteUserFormHandle, InviteUserFormProps>(
	({ onSubmit, defaultValue }, ref) => {
		const methods = useForm({
			resolver: zodResolver(userSchema),
			defaultValues: {
				firstName: "",
				lastName: "",
				email: "",
				phoneNumber: "",
				role: "AUDIOPROSTHESIST",
				access: "MEMBER",
			},
		});
		useEffect(() => {
			if (defaultValue) {
				methods.reset({
					firstName: defaultValue.firstName,
					lastName: defaultValue.lastName,
					email: defaultValue.email,
					phoneNumber: defaultValue.phoneNumber,
					role: defaultValue.role,
					access: defaultValue.access,
				});
			}
		}, [methods, defaultValue]);
		const resetFormFields = async () => {
			methods.reset({
				firstName: "",
				lastName: "",
				email: "",
				phoneNumber: "",
				role: "AUDIOPROSTHESIST",
				access: "MEMBER",
			});
		};
		const handleSubmit = async () => {
			const valid = await methods.trigger();
			if (valid && onSubmit) {
				onSubmit(methods.getValues());
				resetFormFields();
			}
		};
		useImperativeHandle(ref, () => ({
			values: () => methods.getValues(),
			isValid: () => methods.trigger(),
			isDirty: () => {
				return !_.isEqual(methods.getValues(), {
					firstName: "",
					lastName: "",
					email: "",
					phoneNumber: "",
					role: "AUDIOPROSTHESIST",
					access: "MEMBER",
				});
			},
		}));

		return (
			<Flex w="100%">
				<FormProvider {...methods}>
					<form style={{ marginTop: "32px", width: "100%" }}>
						<Flex gap={3}>
							<FormControl isInvalid={!!methods.formState.errors.firstName}>
								<Input
									id="firstName"
									placeholder="Prénom *"
									{...methods.register("firstName")}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.firstName?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.lastName}>
								<Input
									id="lastName"
									placeholder="Nom *"
									{...methods.register("lastName")}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.lastName?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.email}>
								<Input
									id="email"
									placeholder="Email *"
									{...methods.register("email")}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.email?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.phoneNumber}>
								<Input
									id="phoneNumber"
									placeholder="Téléphone"
									{...methods.register("phoneNumber")}
								></Input>
								<FormErrorMessage>
									<>{methods.formState.errors?.phoneNumber?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.access}>
								<Select
									id="access"
									placeholder="Accès"
									defaultValue="MEMBER"
									{...methods.register("access", {
										required: "Ce champ est obligatoire",
									})}
								>
									<option value="ADMIN">Administrateur du centre</option>
									<option value="MEMBER">Membre</option>
								</Select>
								<FormErrorMessage>
									<>{methods.formState.errors?.access?.message}</>
								</FormErrorMessage>
							</FormControl>
							<FormControl isInvalid={!!methods.formState.errors.role}>
								<Select
									id="role"
									placeholder="Rôle"
									{...methods.register("role")}
									defaultValue="AUDIOPROSTHESIST"
								>
									<option value="AUDIOPROSTHESIST">Audioprothésiste</option>
									<option value="ASSISTANT">Assistant</option>
									<option value="OTHER">Autre</option>
								</Select>
								<FormErrorMessage>
									<>{methods.formState.errors?.role?.message}</>
								</FormErrorMessage>
							</FormControl>
						</Flex>

						<Flex w="100%" justifyContent="end" my={5}>
							<Button variant="link" color={"brand.700"} onClick={handleSubmit}>
								<AddIcon mr={3}></AddIcon>
								{defaultValue === undefined && <>Ajouter un utilisateur</>}
								{defaultValue !== undefined && (
									<>Sauvegarder les modifications</>
								)}
							</Button>
						</Flex>
					</form>
				</FormProvider>
			</Flex>
		);
	},
);

export default InviteUserForm;
